import { forwardRef, useEffect, useMemo, useState } from "react";
import { Alert, Box, Typography } from "@mui/material";
import { UsersRound } from "lucide-react";
import { StepProps, StepRef } from "../../types";
import { StepHeader } from "../../components/StepHeader";
import { NextStepButton } from "../../components/NextStepButton";
import TextField from "components/common/TextField";
import PhoneInput from "components/creation/PhoneInput";
import { cleanPhone } from "common/utils";
import { checkPhone } from "common/userUtils";
import useIsMobile from "components/hooks/useIsMobile";
import { useGetMemberDetails, useUpdateMembersFamily } from "services/member";
import { Member } from "protogen/common_pb";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface FormErrors {
  [key: string]: string;
}

export default forwardRef<StepRef, StepProps>(
  ({ onNext, signupState, disabled }, _ref) => {
    const isMobile = useIsMobile();
    const [formData, setFormData] = useState<FormData>({
      firstName: signupState.partnerInfo?.firstName || "",
      lastName: signupState.partnerInfo?.lastName || "",
      email: signupState.partnerInfo?.email || "",
      phone: signupState.partnerInfo?.phone || "",
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [generalError, setGeneralError] = useState("");
    const [previousPhone, setPreviousPhone] = useState(
      signupState.partnerInfo?.phone || "",
    );
    const { data: memberDetails, request: getMemberDetails } =
      useGetMemberDetails();
    const { request: updateFamilyRequest, loading: saveLoading } =
      useUpdateMembersFamily();

    useEffect(() => {
      getMemberDetails();
    }, []);

    const disallowedContacts = useMemo(() => {
      const familyMembers: Member[] =
        memberDetails?.family?.familyMembers || [];
      return {
        emails: familyMembers
          .map((member) => member.primaryEmail)
          .filter(Boolean),
        phones: familyMembers
          .map((member) => member.primaryPhone)
          .filter(Boolean),
      };
    }, [memberDetails?.family?.familyMembers]);

    const hasAnyValue = Object.values(formData).some(
      (value) => value.trim() !== "",
    );

    const setField = (field: keyof FormData, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    };

    const validate = () => {
      if (!hasAnyValue) {
        return {};
      }
      const nextErrors: FormErrors = {};
      if (!formData.firstName.trim()) {
        nextErrors.firstName = "First name is required";
      }
      if (!formData.lastName.trim()) {
        nextErrors.lastName = "Last name is required";
      }
      if (!formData.email.trim()) {
        nextErrors.email = "Email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        nextErrors.email = "Invalid email address";
      } else if (disallowedContacts.emails.includes(formData.email)) {
        nextErrors.email = "This email is already on your household";
      }
      if (!formData.phone.trim()) {
        nextErrors.phone = "Phone is required";
      } else if (!checkPhone(formData.phone)) {
        nextErrors.phone = "Invalid phone number";
      } else if (disallowedContacts.phones.includes(formData.phone)) {
        nextErrors.phone = "This phone number is already on your household";
      }
      return nextErrors;
    };

    const handleContinue = async () => {
      const nextErrors = validate();
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }

      if (!hasAnyValue) {
        await onNext?.();
        return;
      }

      if (!memberDetails?.family?.ref) {
        setGeneralError(
          "We couldn’t load your household yet. Please try again.",
        );
        return;
      }

      setGeneralError("");
      const result = await updateFamilyRequest({
        familyRef: memberDetails.family.ref,
        updatedAddress: memberDetails.family.address,
        updatedMembers: [],
        newMembers: [
          {
            firstName: formData.firstName,
            lastName: formData.lastName,
            primaryPhone: formData.phone,
            primaryEmail: formData.email,
          },
        ],
      });

      if (!result) {
        setGeneralError(
          "Something went wrong adding your partner. Please try again.",
        );
        return;
      }

      await onNext?.({
        partnerInfo: formData,
      });
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          padding: isMobile ? "0 0 32px" : "0 32px",
          gap: isMobile ? "16px" : "24px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "560px",
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? "16px" : "24px",
          }}
        >
          <StepHeader
            title="Add a partner"
            subtitle={
              isMobile ? (
                <Typography variant="body">
                  Add your partner so you can coordinate plans, share reminders,
                  and keep your household on the same page.
                </Typography>
              ) : (
                <>
                  <Typography variant="body">
                    Faye is better together! Each of you can text individually
                    or as a group to coordinate plans, remind one another, and
                    keep the household on the same page.
                  </Typography>
                  <Typography variant="body">
                    You can always do this later if you want to keep moving now.
                  </Typography>
                </>
              )
            }
            alignment="left"
          />

          <Box
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "16px",
              padding: isMobile ? "16px" : "20px",
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
              <UsersRound color="#198282" />
              <Typography variant="bodyHeavy">
                We’ll create their household account access and invite them in
              </Typography>
            </Box>

            {generalError && (
              <Alert severity="error" sx={{ borderRadius: "12px" }}>
                {generalError}
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <TextField
                sx={{ flex: 1, minWidth: isMobile ? "100%" : "unset" }}
                label="First name"
                value={formData.firstName}
                error={!!errors.firstName}
                helperText={errors.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setField("firstName", e.target.value)
                }
              />
              <TextField
                sx={{ flex: 1, minWidth: isMobile ? "100%" : "unset" }}
                label="Last name"
                value={formData.lastName}
                error={!!errors.lastName}
                helperText={errors.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setField("lastName", e.target.value)
                }
              />
            </Box>

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              error={!!errors.email}
              helperText={errors.email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setField("email", e.target.value)
              }
            />

            <PhoneInput
              value={formData.phone}
              error={!!errors.phone}
              helperText={errors.phone}
              onChange={(phone) => {
                let newPhone = cleanPhone(
                  typeof phone === "string" ? phone : phone.target.value,
                );
                if (
                  (!previousPhone || previousPhone.length === 1) &&
                  newPhone.length > 10 &&
                  !newPhone.startsWith("+1")
                ) {
                  newPhone = `+1${newPhone.replace("+", "")}`;
                }
                setField("phone", newPhone);
                setPreviousPhone(newPhone);
              }}
              label="Phone"
              name="partner-phone"
              size="small"
              sx={{ width: "100%" }}
              autoComplete="tel"
            />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <NextStepButton
              skippable={!hasAnyValue}
              hasValue={hasAnyValue}
              disabled={disabled || saveLoading}
              continueClick={handleContinue}
              buttonText={hasAnyValue ? "Invite" : "Skip for now"}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);
